import * as THREE from "three";
import { Line, Polygon, Vector3 } from "opengeometry";
import { Glyphs } from "@opengeometry/openglyph";

import { ElementType } from "../elements/base-type";
import { OPElement, IPlaceable } from "../elements/op-element";
import { IShape } from "../shapes/base-type";
import { Event } from "../../../../utils/event";
import { Y_UP, degToRad, rotatePoint } from "../../../../utils/transform";
import type { Vec3 } from "../types";
import {
  DatumOptionsBase,
  DatumType,
  DATUM_COLORS,
} from "./base-type";
import { buildCircleLine } from "./internals/geometry";
import { applyVisibility, setProfileView, setModelView } from "./internals/visibility";
import { disposeSubElements } from "./internals/lifecycle";

const LABEL_FONT_SIZE = 0.25;

export type CardinalDirection = "N" | "S" | "E" | "W";

export interface ElevationHeadLabel {
  drawingNumber: string;
  sheetNumber: string;
}

export interface ElevationMarkerOptions extends DatumOptionsBase {
  datumType: DatumType.ELEVATION_MARKER;
  /** World-space placement of the marker on the XZ plane. */
  position: [number, number, number];
  /**
   * View direction — either a cardinal preset or an explicit unit
   * vector on the XZ plane.
   */
  direction: CardinalDirection | [number, number];
  /**
   * Render four markers 90° apart (one per cardinal) sharing a single
   * centre, matching the standard four-arrow elevation symbol.
   */
  cluster: boolean;
  /** Head labels shown inside the bubble. */
  head: ElevationHeadLabel;
  /** Identifier of the elevation view this marker generates. */
  linkedViewId?: string;
  /** Outer radius of the bubble in world units. */
  bubbleRadius: number;
}

const CARDINAL_VECTORS: Record<CardinalDirection, [number, number]> = {
  N: [0, -1],
  S: [0, 1],
  E: [1, 0],
  W: [-1, 0],
};

/**
 * Direction-of-view datum for elevations. Generates either a single
 * marker (bubble + filled triangle arrow) or a clustered four-arrow
 * symbol pointing at the four cardinals from a shared centre.
 */
export class ElevationMarker extends OPElement implements IShape, IPlaceable {
  ogid: string = crypto.randomUUID();
  ogType: string = ElementType.DATUM_ELEVATION_MARKER;

  subElements2D: Map<string, THREE.Object3D> = new Map();
  subElements3D: Map<string, THREE.Object3D> = new Map();

  selected = false;
  edit = false;
  locked = false;

  onDatumUpdated: Event<null> = new Event();

  private _profileView = true;
  private _modelView = true;

  propertySet: ElevationMarkerOptions = {
    labelName: "Elevation",
    datumType: DatumType.ELEVATION_MARKER,
    color: DATUM_COLORS.ELEVATION,
    lineWidth: 2,
    visibility: { plan: true, model: true },
    position: [0, 0, 0],
    direction: "N",
    cluster: false,
    head: { drawingNumber: "?", sheetNumber: "?" },
    bubbleRadius: 0.45,
  };

  constructor(config?: Partial<ElevationMarkerOptions>) {
    super();
    if (config) {
      this.propertySet = {
        ...this.propertySet,
        ...config,
        visibility: {
          ...this.propertySet.visibility,
          ...(config.visibility ?? {}),
        },
        head: { ...this.propertySet.head, ...(config.head ?? {}) },
      };
    }
    this.propertySet.ogid = this.ogid;
    this.setOPGeometry();
    const resolved = applyVisibility(
      this.subElements2D,
      this.subElements3D,
      this.propertySet.visibility,
    );
    this._profileView = resolved.plan;
    this._modelView = resolved.model;
  }

  move(delta: Vec3): void {
    const [px, py, pz] = this.propertySet.position;
    this.propertySet.position = [px + delta[0], py + delta[1], pz + delta[2]];
    this.setOPGeometry();
    this.onDatumUpdated.trigger(null);
  }

  rotate(angleDeg: number, axis: Vec3 = Y_UP, anchor?: Vec3): void {
    const rad = degToRad(angleDeg);
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    // Rotate the direction vector around Y.
    const dir = this.resolveDirection(this.propertySet.direction);
    this.propertySet.direction = [
      dir[0] * cos - dir[1] * sin,
      dir[0] * sin + dir[1] * cos,
    ];

    // Rotate position around anchor if provided.
    if (anchor) {
      this.propertySet.position = rotatePoint(this.propertySet.position, rad, axis, anchor);
    }

    this.setOPGeometry();
    this.onDatumUpdated.trigger(null);
  }

  get labelName(): string {
    return this.propertySet.labelName;
  }
  set labelName(value: string) {
    this.propertySet.labelName = value;
    this.setOPGeometry();
  }

  get datumType(): DatumType {
    return this.propertySet.datumType;
  }

  get profileView(): boolean {
    return this._profileView;
  }
  set profileView(value: boolean) {
    this._profileView = value;
    setProfileView(this.subElements2D, value);
  }

  get modelView(): boolean {
    return this._modelView;
  }
  set modelView(value: boolean) {
    this._modelView = value;
    setModelView(this.subElements3D, value);
  }

  setOPConfig(config: Partial<ElevationMarkerOptions>): void {
    disposeSubElements(this.subElements2D, this.subElements3D);
    this.propertySet = {
      ...this.propertySet,
      ...config,
      visibility: {
        ...this.propertySet.visibility,
        ...(config.visibility ?? {}),
      },
      head: { ...this.propertySet.head, ...(config.head ?? {}) },
    };
    this.setOPGeometry();
    this.setOPMaterial();
  }

  getOPConfig(): ElevationMarkerOptions {
    return this.propertySet;
  }

  setOPMaterial(): void {
    // Intentionally empty — geometry rebuilds handle every visual update.
  }

  setOPGeometry(): void {
    disposeSubElements(this.subElements2D, this.subElements3D);

    if (this.propertySet.cluster) {
      // Shared bubble for cluster mode — draw once.
      this.buildSharedBubble();
      (["N", "E", "S", "W"] as CardinalDirection[]).forEach((dir) => {
        this.buildArrow(CARDINAL_VECTORS[dir], dir);
      });
    } else {
      const dir = this.resolveDirection(this.propertySet.direction);
      this.buildSharedBubble();
      this.buildArrow(dir, "single");
    }

    this.onDatumUpdated.trigger(null);
  }

  private resolveDirection(d: CardinalDirection | [number, number]): [number, number] {
    if (typeof d === "string") return CARDINAL_VECTORS[d];
    const [x, z] = d;
    const len = Math.sqrt(x * x + z * z) || 1;
    return [x / len, z / len];
  }

  private buildSharedBubble() {
    const color = this.propertySet.color ?? DATUM_COLORS.ELEVATION;
    const [px, py, pz] = this.propertySet.position;
    const radius = this.propertySet.bubbleRadius;

    const center = new Vector3(px, py, pz);

    const bubble = buildCircleLine(center, radius, color);
    this.add(bubble);
    this.subElements3D.set("bubble", bubble);

    const divider = new Line({
      start: new Vector3(px - radius * 0.9, py, pz),
      end: new Vector3(px + radius * 0.9, py, pz),
      color,
      fatLines: true,
      width: 1,
    });
    this.add(divider);
    this.subElements3D.set("divider", divider);

    // Drawing number in upper half (−Z), sheet number in lower half (+Z).
    const drawingGlyph = Glyphs.addGlyph(
      this.propertySet.head.drawingNumber,
      LABEL_FONT_SIZE,
      color,
      true,
    );
    drawingGlyph.textMesh.geometry.computeBoundingBox();
    const dbox = drawingGlyph.textMesh.geometry.boundingBox!;
    const dhw = (dbox.max.x - dbox.min.x) * 0.5;
    const dhd = (dbox.max.y - dbox.min.y) * 0.5;
    drawingGlyph.position.set(px - dhw, py, pz - radius * 0.3 - dhd);
    this.add(drawingGlyph);
    this.subElements3D.set("labelDrawing", drawingGlyph);

    const sheetGlyph = Glyphs.addGlyph(
      this.propertySet.head.sheetNumber,
      LABEL_FONT_SIZE,
      color,
      true,
    );
    sheetGlyph.textMesh.geometry.computeBoundingBox();
    const sbox = sheetGlyph.textMesh.geometry.boundingBox!;
    const shw = (sbox.max.x - sbox.min.x) * 0.5;
    const shd = (sbox.max.y - sbox.min.y) * 0.5;
    sheetGlyph.position.set(px - shw, py, pz + radius * 0.3 - shd);
    this.add(sheetGlyph);
    this.subElements3D.set("labelSheet", sheetGlyph);
  }

  private buildArrow(direction: [number, number], keyPrefix: string) {
    const color = this.propertySet.color ?? DATUM_COLORS.ELEVATION;
    const [px, py, pz] = this.propertySet.position;
    const [ux, uz] = direction;
    const radius = this.propertySet.bubbleRadius;

    // Filled triangle: tip at 2× radius, wings at 1.2× radius ± 0.4× perpendicular.
    const tipVec = new Vector3(px + ux * radius * 2.0, py, pz + uz * radius * 2.0);
    const wingA = new Vector3(
      px + ux * radius * 1.2 - uz * radius * 0.4,
      py,
      pz + uz * radius * 1.2 + ux * radius * 0.4,
    );
    const wingB = new Vector3(
      px + ux * radius * 1.2 + uz * radius * 0.4,
      py,
      pz + uz * radius * 1.2 - ux * radius * 0.4,
    );

    const arrow = new Polygon({ vertices: [tipVec, wingA, wingB], color });
    this.add(arrow);
    this.subElements3D.set(`${keyPrefix}-arrow`, arrow);
  }
}
